import { type AppMode, type ModeFlags, flagsOf, resolveMode } from './app/mode';
import {
  DEFAULT_ENGINE_RULES,
  type EngineRules,
  RULES_META,
  type RuleKey,
  SEED_ALPHABET,
  SEED_LENGTH,
  type ViewRules,
  isValidSeed,
  normalizeSeed,
  rulesHash,
} from './engine';
import { type ChallengeLink, packVersionOf, parseChallengeLink } from './share/link';
import { presetOf } from './telemetry/env';
import type { Preset } from './telemetry/types';
import { DEFAULT_VIEW_RULES } from './view/rules';

/** 프리셋별 DISTRACTOR_COUNT — 참가자 코드 파생 층화 변수(SPEC §15.2). */
export const PRESET_DISTRACTORS: Readonly<Record<Preset, number>> = { A: 6, B: 8, C: 12 };

/**
 * 빌드 변종 — RULES §16 #8 의 `DECOY_ON=false` 재측정 자리.
 * `VITE_DECOY_ON=0` 빌드에서만 'no-decoy' 가 되고 URL 로는 바꿀 수 없다(decoyOn 은 fixed).
 * rulesOverridden 에 실리지 않는다 — 변종은 오버라이드가 아니라 별도 등록 대상이고,
 * engineRulesHash 가 달라지므로 G0.md 에 변종 해시가 등록되기 전에는 자동 제외된다.
 */
export type Variant = 'default' | 'no-decoy';

export interface OverrideReport {
  readonly applied: readonly string[];
  readonly rejected: readonly string[];
  readonly repaired: readonly string[];
}

export interface BootConfig {
  readonly mode: AppMode;
  readonly flags: ModeFlags;
  readonly seed: string;
  /** 팩 id — `?pack=` → 링크 `pk` → 빌드 기본(`VITE_DEFAULT_PACK`, 없으면 core) */
  readonly packId: string;
  /** 도전장 링크가 못 박은 팩 버전(없으면 null = 최신) */
  readonly packPinned: string | null;
  readonly participant: string | null;
  readonly preset: Preset;
  readonly debug: boolean;
  readonly engineRules: EngineRules;
  readonly viewRules: ViewRules;
  readonly engineRulesHash: string;
  readonly viewRulesHash: string;
  readonly rulesOverridden: boolean;
  readonly variant: Variant;
  /** 도전장 링크(ADR-028) — `s`·`r` 이 있으면 그 판으로 부팅한다. 웹은 대결 한정 모드 */
  readonly challenge: ChallengeLink | null;
  readonly runIndex: number; // 도전장이면 그 판 순번, 아니면 0
  readonly report: OverrideReport;
}

export function randomSessionSeed(): string {
  const buf = new Uint32Array(SEED_LENGTH);
  crypto.getRandomValues(buf);
  let out = '';
  for (let i = 0; i < SEED_LENGTH; i++) out += SEED_ALPHABET.charAt((buf[i] as number) % 32);
  return out;
}

/** `VITE_DECOY_ON` 은 정확히 '0' 일 때만 no-decoy 다 — 비움·오타·'false' 는 전부 기본 변종. */
export function variantOf(decoyOnEnv: string | undefined): Variant {
  return decoyOnEnv === '0' ? 'no-decoy' : 'default';
}

/**
 * URL → BootConfig. 예외를 던지지 않는다 — 링크는 손으로 타이핑되고,
 * 오타 하나로 흰 화면이 되면 G0 세션이 죽는다. 진단은 report 로 남긴다.
 *
 * decoyOnEnv 는 빌드 상수 `import.meta.env.VITE_DECOY_ON` 이다 — 기본값으로만 읽고 인자로 넘기면
 * 순수 함수로 남는다(테스트·도구). tsx 처럼 import.meta.env 가 없는 런타임에서는 undefined 다.
 */
export function readBootConfig(
  href: string,
  fallbackSeed: string,
  appModeEnv?: string,
  decoyOnEnv: string | undefined = import.meta.env?.VITE_DECOY_ON,
  defaultPack = 'core',
): BootConfig {
  const mode = resolveMode(appModeEnv);
  const flags = flagsOf(mode);
  const variant = variantOf(decoyOnEnv);
  const applied: string[] = [];
  const rejected: string[] = [];
  const repaired: string[] = [];
  let params: URLSearchParams;
  try {
    params = new URL(href).searchParams;
  } catch {
    params = new URLSearchParams();
  }

  const challenge = parseChallengeLink(params);
  const rawSeed = params.get('s') ?? params.get('seed');
  const seed = rawSeed !== null && isValidSeed(rawSeed) ? normalizeSeed(rawSeed) : fallbackSeed;
  if (rawSeed !== null && !isValidSeed(rawSeed)) rejected.push(`seed:${rawSeed}`);

  const debug = params.get('debug') === '1';
  // 참가자 코드는 연구 빌드에서만 읽는다 — 제품 빌드의 `?p=` 는 없는 것과 같다(RULES §1.13 APP_MODE)
  const participant = mode === 'research' ? params.get('p') : null;
  const preset = presetOf(participant);
  // 프리셋 층화(오답 6/8/12)는 연구 모드 전용이다. 제품 모드는 RULES 확정값 그대로.
  // 변종(no-decoy)은 빌드 상수라 모드와 무관하게 적용된다 — 같은 빌드의 모든 세션이 같은 변종이다.
  const base: EngineRules =
    variant === 'no-decoy' ? { ...DEFAULT_ENGINE_RULES, decoyOn: false } : DEFAULT_ENGINE_RULES;
  const engine: Record<string, unknown> = flags.presetAssignment
    ? { ...base, distractorCount: PRESET_DISTRACTORS[preset] }
    : { ...base };
  const view: Record<string, unknown> = { ...DEFAULT_VIEW_RULES };

  if (debug) {
    for (const [key, raw] of params.entries()) {
      if (key === 's' || key === 'seed' || key === 'debug' || key === 'pack' || key === 'p')
        continue;
      const meta = RULES_META[key as RuleKey] as (typeof RULES_META)[RuleKey] | undefined;
      if (meta === undefined) {
        rejected.push(`${key}:unknown`);
        continue;
      }
      if (meta.kind === 'fixed') {
        rejected.push(`${key}:fixed`);
        continue;
      }
      // 배열 키(tickMsTable·growPeriodTable)는 URL 스칼라로 덮으면 createState 의 길이 어서션이 throw 한다 —
      // 파서는 스칼라만 다루므로 거절하고 진단만 남긴다(RULES §12.1: URL 로 넘길 수 없음).
      if (Array.isArray((meta.scope === 'engine' ? engine : view)[key])) {
        rejected.push(`${key}:array`);
        continue;
      }
      if (meta.kind === 'bool') {
        if (raw !== '0' && raw !== '1' && raw !== 'true' && raw !== 'false') {
          rejected.push(`${key}:type`);
          continue;
        }
        const val = raw === '1' || raw === 'true';
        if (meta.scope === 'engine') engine[key] = val;
        else view[key] = val;
        applied.push(`${key}=${val}`);
        continue;
      }
      const n = Number(raw);
      const ok = meta.kind === 'int' ? Number.isInteger(n) : Number.isFinite(n);
      if (!ok) {
        rejected.push(`${key}:type`);
        continue;
      }
      const clamped = Math.min(meta.max, Math.max(meta.min, n));
      if (clamped !== n) repaired.push(`${key}:${n}→${clamped}`);
      if (meta.scope === 'engine') engine[key] = clamped;
      else view[key] = clamped;
      applied.push(`${key}=${clamped}`);
    }
  } else if ([...params.keys()].some((k) => k in RULES_META)) {
    rejected.push('no-debug');
  }

  const engineRules = engine as unknown as EngineRules;
  const viewRules = view as unknown as ViewRules;
  return {
    mode,
    flags,
    seed,
    packId: params.get('pack') ?? challenge?.pack ?? defaultPack, // 도전장 링크의 pk 가 팩을 정한다(ADR-036)
    packPinned: challenge === null ? null : packVersionOf(challenge),
    participant,
    preset,
    debug,
    engineRules,
    viewRules,
    engineRulesHash: rulesHash(engineRules),
    viewRulesHash: rulesHash(viewRules),
    rulesOverridden: applied.length > 0,
    variant,
    challenge,
    runIndex: challenge?.runIndex ?? 0,
    report: { applied, rejected, repaired },
  };
}
